import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { losApi, LOAN_STATUS_LABELS, PROGRAM_LABELS, statusColorHex } from './losApi';
import {
  Plus, RefreshCw, MoreHorizontal, Lock, LockOpen,
  Filter, Loader2, Search, Diamond,
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogTitle } from '../components/ui/dialog';
import { toast } from '../hooks/use-toast';
import { confirmDialog } from '@/components/dialogs/DialogHost';
import ContactPicker from './shared/ContactPicker';
import { copyText } from './clipboard';
import { errorMessage } from './errors';
import PhoneInput from './shared/PhoneInput';
import MoneyInput from './shared/MoneyInput';
import NumberInput from './shared/NumberInput';
import AddressFields from './shared/AddressFields';
import { emptyAddress, addressPayload } from './shared/address';
import { LOAN_PROGRAM_LABELS, LOAN_PURPOSE_LABELS, OCCUPANCY_LABELS, PROPERTY_TYPE_LABELS, optionsFrom } from './shared/labels';
import { formatDate, fromApi } from './kit/time';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { OwnerSelect, useStaff } from './kit/OwnerChip';
import AriveBadge from './board/AriveBadge';
import { ariveDeepLink } from './ariveManaged';

const fmt$ = (n) => Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const fmtRel = (iso) => {
  if (!iso) return '—';
  const s = Math.floor((Date.now() - fromApi(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s/60)}m ago`;
  if (s < 86400) return `${Math.floor(s/3600)}h ago`;
  return `${Math.floor(s/86400)}d ago`;
};

// Group loan statuses into 5 top-level filter chips (Arive-style)
const CHIP_GROUPS = {
  All: null,
  Prospect: ['prospect', 'pre_qual', 'pre_approved'],
  Processing: ['application_received', 'processing', 'submitted_to_uw'],
  Closing: ['conditional_approval', 'clear_to_close', 'closing_scheduled'],
  Funded: ['funded'],
};

const PAGE_SIZE = 50;
const ARCHIVED_CHIP = 'Archived';

// Ticket 192 row 4: the advanced filters, as in Arive's pipeline. URL param -> label.
const ADVANCED_KEYS = ['lo', 'program', 'source', 'date_field', 'date_from', 'date_to', 'amount_min', 'amount_max'];
const NO_ADVANCED = Object.fromEntries(ADVANCED_KEYS.map((k) => [k, '']));

export default function LoanFilesPage() {
  const [loans, setLoans] = useState([]);
  const [total, setTotal] = useState(0);
  const [statusCounts, setStatusCounts] = useState({});
  const [archivedCount, setArchivedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);   // message of the last failed list load
  // Ticket 188 row 7: the contact profile opens this with ?new=1&contact_id=...
  // Ticket 192: every filter lives in the URL too, so a filtered view can be reloaded or shared.
  const [searchParams, setSearchParams] = useSearchParams();
  const startContactId = searchParams.get('new') === '1' ? searchParams.get('contact_id') : null;
  const [showNew, setShowNew] = useState(searchParams.get('new') === '1');
  // Archived loans (D29) sit behind their own chip; a search still finds them in the other chips.
  const archivedView = searchParams.get('chip') === ARCHIVED_CHIP;
  const chip = CHIP_GROUPS[searchParams.get('chip')] !== undefined ? searchParams.get('chip') : 'All';
  const statusFilter = searchParams.get('status') || 'all';
  const channelFilter = searchParams.get('channel') || 'all';
  const sort = searchParams.get('sort') || 'updated_desc';
  const searchText = searchParams.get('q') || '';
  const pageNo = Math.max(1, Number(searchParams.get('page')) || 1);
  const advanced = Object.fromEntries(ADVANCED_KEYS.map((k) => [k, searchParams.get(k) || '']));
  const advancedCount = [
    advanced.lo, advanced.program, advanced.source,
    advanced.date_from || advanced.date_to, advanced.amount_min || advanced.amount_max,
  ].filter(Boolean).length;
  // Typing updates the box at once; the URL (and the fetch) follow after a pause.
  const [searchBox, setSearchBox] = useState(searchText);
  useEffect(() => { setSearchBox(searchText); }, [searchText]);

  const setParams = (changes, { keepPage = false } = {}) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(changes).forEach(([k, v]) => {
        if (v === null || v === undefined || v === '' || v === 'all') next.delete(k);
        else next.set(k, String(v));
      });
      if (!keepPage) next.delete('page');
      return next;
    }, { replace: true });
  };
  useEffect(() => {
    if (searchBox === searchText) return undefined;
    const t = setTimeout(() => setParams({ q: searchBox.trim() }), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchBox, searchText]);

  const query = useMemo(() => {
    const params = { sort, skip: (pageNo - 1) * PAGE_SIZE, limit: PAGE_SIZE };
    if (archivedView) params.archived = 'only';
    else if (CHIP_GROUPS[chip]) params.statuses = CHIP_GROUPS[chip].join(',');
    if (statusFilter !== 'all') params.status = statusFilter;
    if (channelFilter !== 'all') params.channel = channelFilter;
    if (searchText) params.q = searchText;
    if (advanced.lo) params.owner_id = advanced.lo;
    if (advanced.program) params.program = advanced.program;
    if (advanced.source) params.source = advanced.source;
    if (advanced.date_from || advanced.date_to) {
      params.date_field = advanced.date_field || 'application';
      if (advanced.date_from) params.date_from = advanced.date_from;
      if (advanced.date_to) params.date_to = advanced.date_to;
    }
    if (advanced.amount_min) params.amount_min = advanced.amount_min;
    if (advanced.amount_max) params.amount_max = advanced.amount_max;
    return params;
    // `advanced` is rebuilt every render; the URL string is what changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chip, archivedView, statusFilter, channelFilter, sort, searchText, pageNo, searchParams.toString()]);

  // A slow answer for an old filter must not overwrite the answer for the current one.
  const latest = useRef(0);
  const load = async () => {
    const mine = ++latest.current;
    setLoadError(null);
    try {
      const body = await losApi.listLoanPage(query);
      if (mine !== latest.current) return;
      setLoans(body.items);
      setTotal(body.total);
      setStatusCounts(body.status_counts || {});
      setArchivedCount(body.archived_count || 0);
    } catch (e) {
      if (mine !== latest.current) return;
      setLoadError(errorMessage(e, 'Could not load loans'));
    } finally {
      if (mine === latest.current) setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [query]);
  // The top-bar + menu can land here while the page is already open.
  useEffect(() => { if (searchParams.get('new') === '1') setShowNew(true); }, [searchParams]);
  const closeNew = () => {
    setShowNew(false);
    if (searchParams.has('new')) setParams({ new: null, contact_id: null }, { keepPage: true });
  };

  // Counts per chip, from the status counts the server computed for this search + channel
  const counts = useMemo(() => {
    const c = { All: Object.values(statusCounts).reduce((a, n) => a + n, 0) };
    Object.entries(CHIP_GROUPS).forEach(([k, statuses]) => {
      if (k === 'All') return;
      c[k] = statuses.reduce((a, s) => a + (statusCounts[s] || 0), 0);
    });
    return c;
  }, [statusCounts]);

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  // The last row of the last page was deleted: step back instead of showing an empty page.
  useEffect(() => {
    if (!loading && !loadError && loans.length === 0 && pageNo > pageCount) {
      setParams({ page: pageCount }, { keepPage: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, loadError, loans, pageNo, pageCount]);
  const hasFilters = chip !== 'All' || archivedView || statusFilter !== 'all' || channelFilter !== 'all' || !!searchText || advancedCount > 0;

  return (
    <div className="max-w-[1600px] mx-auto px-4 md:px-6 py-5">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-3">
          <h1 className="font-serif text-2xl font-bold" style={{ color: '#030524' }}>Loans</h1>
          <span className="text-sm text-slate-500" data-testid="loans-total">{counts.All} total</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} data-testid="refresh-loans" className="w-9 h-9 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 flex items-center justify-center text-slate-600">
            <RefreshCw size={14} />
          </button>
          <button
            onClick={() => setShowNew(true)}
            data-testid="new-loan-btn"
            className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-lg text-white text-sm font-semibold hover:opacity-90 transition shadow-sm"
            style={{ background: '#070B5F' }}
          >
            <Plus size={14} /> Loan
          </button>
        </div>
      </div>

      {/* Chip filter row */}
      <div className="flex items-center gap-1.5 mb-3 overflow-x-auto pb-1" data-testid="chip-filter-row">
        {Object.keys(CHIP_GROUPS).map((k) => {
          const active = !archivedView && chip === k;
          return (
            <button
              key={k}
              onClick={() => setParams({ chip: k })}
              data-testid={`chip-${k.toLowerCase()}`}
              className={`inline-flex items-center gap-2 h-8 px-3.5 rounded-full text-[13px] font-semibold whitespace-nowrap transition ${
                active
                  ? 'text-white shadow-sm'
                  : 'text-slate-600 bg-white border border-slate-200 hover:bg-slate-50'
              }`}
              style={active ? { background: 'linear-gradient(135deg, #070B5F 0%, #0f1580 100%)' } : {}}
            >
              {k}
              <span className={`text-[11px] font-bold rounded-full px-1.5 py-0.5 ${active ? 'text-white' : 'bg-slate-100 text-slate-500'}`} style={active ? { background: '#6BA82E' } : {}}>
                {counts[k] || 0}
              </span>
            </button>
          );
        })}
        <button
          onClick={() => setParams({ chip: archivedView ? null : ARCHIVED_CHIP })}
          data-testid="chip-archived"
          aria-pressed={archivedView}
          className={`inline-flex items-center gap-2 h-8 px-3.5 rounded-full text-[13px] font-semibold whitespace-nowrap transition ${
            archivedView ? 'text-white shadow-sm' : 'text-slate-600 bg-white border border-slate-200 hover:bg-slate-50'
          }`}
          style={archivedView ? { background: 'linear-gradient(135deg, #070B5F 0%, #0f1580 100%)' } : {}}
        >
          Archived
          <span className={`text-[11px] font-bold rounded-full px-1.5 py-0.5 ${archivedView ? 'text-white' : 'bg-slate-100 text-slate-500'}`} style={archivedView ? { background: '#6BA82E' } : {}}>
            {archivedCount}
          </span>
        </button>
      </div>

      {/* Advanced filters row */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 basis-full sm:basis-auto min-w-[220px] max-w-md">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={searchBox}
            onChange={(e) => setSearchBox(e.target.value)}
            placeholder="Search by borrower, loan #, or address…"
            className="pl-8 h-9 text-[13px]"
            data-testid="loans-search"
          />
        </div>
        <FilterSelect
          testId="status-filter"
          value={statusFilter}
          onChange={(v) => setParams({ status: v })}
          label="All Statuses"
          options={[['all','All Statuses'], ...Object.entries(LOAN_STATUS_LABELS)]}
        />
        <FilterSelect
          testId="channel-filter"
          value={channelFilter}
          onChange={(v) => setParams({ channel: v })}
          label="Any Channel"
          options={[['all','Any Channel'],['retail','Retail'],['wholesale','Wholesale'],['correspondent','Correspondent']]}
        />
        <AdvancedFilters values={advanced} count={advancedCount} onApply={(v) => setParams(v)} />
        <div className="hidden sm:block flex-1" />
        <FilterSelect
          testId="sort-select"
          value={sort}
          onChange={(v) => setParams({ sort: v === 'updated_desc' ? null : v })}
          label="Sort"
          options={[['updated_desc','Last Updated ↓'],['updated_asc','Last Updated ↑'],['amount_desc','Amount ↓'],['amount_asc','Amount ↑']]}
        />
      </div>

      {loading ? (
        <div className="text-slate-500 flex items-center gap-2 p-8"><Loader2 className="animate-spin" size={16} /> Loading…</div>
      ) : loadError ? (
        <div role="alert" className="bg-red-50 border border-red-100 rounded-2xl p-12 text-center text-red-700" data-testid="loans-error">
          <p className="text-sm">Couldn’t load loans.</p>
          <p className="text-xs mt-1">{loadError}</p>
          <button onClick={load} data-testid="loans-retry" className="mt-4 inline-flex items-center h-9 px-3.5 rounded-lg border border-red-200 bg-white text-red-700 text-sm font-semibold hover:bg-red-50">Retry</button>
        </div>
      ) : loans.length === 0 ? (
        <EmptyState onCreate={() => setShowNew(true)} hasAny={hasFilters} />
      ) : (
        <>
          <LoanTable loans={loans} onRefresh={load} />
          <div className="flex items-center justify-between mt-3 text-[12px] text-slate-500" data-testid="loans-pager">
            <span>
              Showing {(pageNo - 1) * PAGE_SIZE + 1}–{(pageNo - 1) * PAGE_SIZE + loans.length} of {total}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setParams({ page: pageNo - 1 }, { keepPage: true })}
                disabled={pageNo <= 1}
                data-testid="loans-prev"
                className="h-8 px-3 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40"
              >Previous</button>
              <span>Page {pageNo} of {pageCount}</span>
              <button
                onClick={() => setParams({ page: pageNo + 1 }, { keepPage: true })}
                disabled={pageNo >= pageCount}
                data-testid="loans-next"
                className="h-8 px-3 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40"
              >Next</button>
            </div>
          </div>
        </>
      )}

      {showNew && <NewLoanModal initialContactId={startContactId} onClose={closeNew} onCreated={() => { closeNew(); load(); }} />}
    </div>
  );
}

function AdvancedFilters({ values, count, onApply }) {
  const staff = useStaff();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(values);
  // Opening the panel starts from what the URL says now.
  const onOpenChange = (next) => {
    if (next) setDraft(values);
    setOpen(next);
  };
  const set = (k) => (v) => setDraft((d) => ({ ...d, [k]: v ?? '' }));
  const apply = (v) => { onApply(v); setOpen(false); };
  const field = 'h-9 text-[13px] bg-white';
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border bg-white text-[13px] hover:bg-slate-50 ${count ? 'border-slate-400 text-slate-800' : 'border-slate-200 text-slate-600'}`}
          data-testid="advanced-filter"
        >
          <Filter size={13} /> Advanced{count ? ` (${count})` : ''}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 space-y-3" data-testid="advanced-panel">
        <div>
          <Label className="text-[12px] text-slate-500">Loan officer</Label>
          <OwnerSelect
            value={draft.lo || 'all'}
            staff={staff}
            onChange={(v) => set('lo')(v === 'all' ? '' : v)}
            extra={[['all', 'Any loan officer']]}
            testId="adv-lo"
            className={field}
          />
        </div>
        <div>
          <Label className="text-[12px] text-slate-500">Program</Label>
          <FilterSelect
            testId="adv-program"
            value={draft.program || 'all'}
            onChange={(v) => set('program')(v === 'all' ? '' : v)}
            options={[['all', 'Any program'], ...Object.entries(LOAN_PROGRAM_LABELS)]}
          />
        </div>
        <div>
          <Label className="text-[12px] text-slate-500">Source</Label>
          <FilterSelect
            testId="adv-source"
            value={draft.source || 'all'}
            onChange={(v) => set('source')(v === 'all' ? '' : v)}
            options={[['all', 'Arive-linked or BFY'], ['arive', 'Arive-linked'], ['bfy', 'BFY-originated']]}
          />
        </div>
        <div>
          <Label className="text-[12px] text-slate-500">Date range</Label>
          <FilterSelect
            testId="adv-date-field"
            value={draft.date_field || 'application'}
            onChange={set('date_field')}
            options={[['application', 'Application date'], ['closing', 'Closing date']]}
          />
          <div className="flex gap-2 mt-2">
            <Input type="date" aria-label="From date" className={field} value={draft.date_from} onChange={(e) => set('date_from')(e.target.value)} data-testid="adv-date-from" />
            <Input type="date" aria-label="To date" className={field} value={draft.date_to} onChange={(e) => set('date_to')(e.target.value)} data-testid="adv-date-to" />
          </div>
        </div>
        <div>
          <Label className="text-[12px] text-slate-500">Loan amount</Label>
          <div className="flex gap-2">
            <MoneyInput placeholder="Min" aria-label="Minimum amount" className={field} value={draft.amount_min === '' ? null : Number(draft.amount_min)} onChange={(v) => set('amount_min')(v === null || v < 0 ? '' : v)} data-testid="adv-amount-min" />
            <MoneyInput placeholder="Max" aria-label="Maximum amount" className={field} value={draft.amount_max === '' ? null : Number(draft.amount_max)} onChange={(v) => set('amount_max')(v === null || v < 0 ? '' : v)} data-testid="adv-amount-max" />
          </div>
        </div>
        <div className="flex justify-between pt-1">
          <button className="text-[13px] text-slate-500 hover:text-slate-800" onClick={() => apply(NO_ADVANCED)} data-testid="adv-clear">Clear</button>
          <button className="h-8 px-3 rounded-lg text-[13px] text-white" style={{ background: '#030524' }} onClick={() => apply(draft)} data-testid="adv-apply">Apply</button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function FilterSelect({ value, onChange, options, testId }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-9 text-[13px] w-auto min-w-[160px] bg-white" data-testid={testId}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

// =========================================================================
// TABLE
// =========================================================================
function LoanTable({ loans, onRefresh }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="loans-table">
      <div className="overflow-x-auto">
        {/* Scrolls sideways on phones instead of crushing the columns. */}
        <table className="w-full min-w-[860px] md:min-w-0 text-[13px]">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 text-[11px] font-semibold uppercase tracking-wider">
              <Th className="w-[26%]">Borrowers / Loan # / Subject Property</Th>
              <Th className="w-[18%]">Loan Status / Trackers</Th>
              <Th className="w-[9%]">Tasks Due</Th>
              <Th className="w-[15%]">Product / Lender</Th>
              <Th className="w-[12%] text-right">Loan Amount</Th>
              <Th className="w-[13%]">Lock Status</Th>
              <Th className="w-[7%]" />
            </tr>
          </thead>
          <tbody>
            {loans.map((l) => <LoanRow key={l.loan_file_id} loan={l} onRefresh={onRefresh} />)}
          </tbody>
        </table>
      </div>
    </div>
  );
}
function Th({ children, className = '' }) {
  return <th className={`text-left px-4 py-3 font-semibold ${className}`}>{children}</th>;
}

function LoanRow({ loan, onRefresh }) {
  const navigate = useNavigate();
  const b = (loan.urla?.borrower) || {};
  const name = [b.first_name, b.last_name].filter(Boolean).join(' ') || 'Unnamed borrower';
  const addr = loan.property_address || {};
  const cityState = [addr.city, addr.state].filter(Boolean).join(', ');
  // Ticket 256: loan_amount is the total loan (financed UFMIP / VA fee included), so a computed
  // LTV would be too high. Prefer the stored base-loan LTV.
  const ltv = typeof loan.ltv === 'number' ? Math.round(loan.ltv) : loan.appraised_value ? Math.round((loan.loan_amount / loan.appraised_value) * 100) : (loan.purchase_price ? Math.round((loan.loan_amount / loan.purchase_price) * 100) : null);
  const openConds = (loan.conditions_open_count ?? 0);
  const tasksDue = (loan.tasks_due_count ?? 0);
  const locked = loan.rate_lock?.is_locked;
  const rate = loan.rate_lock?.rate || loan.note_rate;
  const expiresIso = loan.rate_lock?.expiration_date;
  const productLabel = loan.rate_lock?.product || `${loan.term_months ? loan.term_months/12 + ' Yr' : ''} ${PROGRAM_LABELS[loan.loan_program] || ''}`.trim();
  const lender = loan.rate_lock?.lender || '—';
  const statusLabel = LOAN_STATUS_LABELS[loan.status] || loan.status;

  const handleCopyLink = async () => {
    const link = `${window.location.origin}/loan/${loan.share_token}`;
    if (await copyText(link)) toast({ title: 'Link copied' });
    else toast({ title: 'Copy failed', description: 'Select the text and copy it manually' });
  };

  const handleDelete = async () => {
    if (!(await confirmDialog({ title: 'Delete this loan file?', confirmText: 'Delete', destructive: true }))) return;
    try {
      await losApi.deleteLoan(loan.loan_file_id);
      toast({ title: 'Loan deleted' });
      onRefresh && onRefresh();
    } catch (e) {
      toast({ title: 'Delete failed', description: errorMessage(e) });
    }
  };

  return (
    <tr className="border-b border-slate-100 hover:bg-slate-50/60 transition group" data-testid={`loan-row-${loan.loan_number}`}>
      {/* Borrowers / Loan # / Subject Property */}
      <td className="px-4 py-3 align-top">
        <Link to={`/portal/loans/${loan.loan_file_id}`} className="block group/link">
          <div className="flex items-center gap-2">
            <StatusDot status={loan.status} />
            <span className="font-semibold text-[13px] group-hover/link:underline" style={{ color: '#030524' }}>{name}</span>
          </div>
          <div className="text-[11px] text-slate-500 font-mono mt-0.5">{loan.loan_number}</div>
          <div className="text-[12px] text-slate-500 mt-0.5 truncate max-w-[320px]">
            {addr.street || '—'}{cityState && ` · ${cityState}`}
          </div>
        </Link>
        {loan.archived_at && (
          <span data-testid="archived-badge" className="inline-block mt-1.5 mr-1.5 px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[11px] font-semibold">Archived</span>
        )}
        {loan.arive && <div className="mt-1.5"><AriveBadge deepLink={ariveDeepLink(loan)} /></div>}
      </td>

      {/* Loan Status / Trackers */}
      <td className="px-4 py-3 align-top">
        <div className="flex items-start gap-1.5">
          <Diamond size={11} className="mt-0.5 shrink-0" style={{ color: statusColorHex(loan.status) }} fill={statusColorHex(loan.status)} />
          <div className="min-w-0">
            <div className="font-semibold text-[12px]" style={{ color: '#030524' }}>{statusLabel}</div>
            <div className="flex items-center gap-2 mt-1">
              {trackerDots(loan).map((d) => <TrackerDot key={d.label} {...d} />)}
            </div>
          </div>
        </div>
      </td>

      {/* Tasks Due */}
      <td className="px-4 py-3 align-top">
        <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-slate-200 bg-white text-[12px]">
          <span className={`font-bold ${tasksDue > 0 ? 'text-red-600' : 'text-slate-500'}`}>{tasksDue}</span>
          <span className="text-slate-400">·</span>
          <span className={`text-[11px] ${openConds > 0 ? 'text-amber-600 font-semibold' : 'text-slate-400'}`}>{openConds} cond</span>
        </div>
      </td>

      {/* Product / Lender */}
      <td className="px-4 py-3 align-top">
        <div className="text-[12px] font-semibold truncate max-w-[180px]" style={{ color: '#030524' }}>{productLabel}</div>
        <div className="text-[11px] text-slate-500 mt-0.5 truncate max-w-[180px]">{lender}</div>
      </td>

      {/* Loan Amount */}
      <td className="px-4 py-3 align-top text-right">
        <div className="font-semibold text-[13px]" style={{ color: '#030524' }}>{fmt$(loan.loan_amount)}</div>
        <div className="text-[11px] text-slate-500 mt-0.5 uppercase tracking-wider">
          {loan.loan_purpose === 'purchase' ? 'Purchase' : loan.loan_purpose?.includes('refi') ? 'Refi' : loan.loan_purpose}
          {ltv && ` · ${ltv}%`}
        </div>
      </td>

      {/* Lock Status */}
      <td className="px-4 py-3 align-top">
        <div className="flex items-center gap-1.5">
          {locked
            ? <Lock size={12} style={{ color: '#6BA82E' }} />
            : <LockOpen size={12} className="text-slate-400" />
          }
          <span className="font-semibold text-[12px]" style={{ color: '#030524' }}>{rate ? `${rate}%` : '—'}</span>
        </div>
        <div className={`text-[11px] mt-0.5 ${locked ? 'text-emerald-600' : 'text-slate-400'}`}>
          {locked && expiresIso
            ? `Exp ${formatDate(expiresIso)}`
            : 'Not Locked'}
        </div>
      </td>

      {/* Actions */}
      <td className="px-4 py-3 align-top text-right">
        <div className="inline-flex items-center gap-1 opacity-70 group-hover:opacity-100 transition">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="w-7 h-7 rounded hover:bg-slate-100 flex items-center justify-center text-slate-500" title="More" aria-label={`More actions for ${loan.loan_number}`} data-testid="loan-row-menu-trigger">
                <MoreHorizontal size={13} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => navigate(`/portal/loans/${loan.loan_file_id}`)} data-testid="loan-row-menu-open">
                Open
              </DropdownMenuItem>
              {loan.share_token && (
                <DropdownMenuItem onClick={handleCopyLink} data-testid="loan-row-menu-copy-link">
                  Copy share link
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={handleDelete} className="text-red-600 focus:text-red-600 focus:bg-red-50" data-testid="loan-row-menu-delete">
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div className="text-[10px] text-slate-400 mt-1 whitespace-nowrap">{fmtRel(loan.updated_at || loan.created_at)}</div>
      </td>
    </tr>
  );
}

function StatusDot({ status }) {
  return <span className="w-2 h-2 rounded-full shrink-0" style={{ background: statusColorHex(status) }} />;
}
// Ticket 192: an Arive-linked loan shows Arive's own trackers (a step is "on" once Arive has
// a date for it); the title carries Arive's status text. A BFY-originated loan has no
// tracker dates to show yet, so its dots stay off.
export function trackerDots(loan) {
  const arive = loan.arive;
  const tracker = (name) => (arive?.trackers || []).find((t) => t.name === name);
  const fromTracker = (label, name) => {
    const t = tracker(name);
    const detail = t ? [t.status, t.date ? formatDate(t.date) : null].filter(Boolean).join(' · ') : '';
    return { label, on: !!t?.date, title: detail ? `${label}: ${detail}` : undefined };
  };
  const itp = arive?.key_dates?.intentToProceedDate;
  return [
    { label: 'ITP', on: !!itp, title: itp ? `ITP: ${formatDate(itp)}` : undefined },
    fromTracker('APR', 'APPRAISAL'),
    fromTracker('CD', 'CD'),
  ];
}
function TrackerDot({ label, on, title }) {
  return (
    <span title={title} className="inline-flex items-center gap-0.5 text-[10px] font-semibold" style={{ color: on ? '#6BA82E' : '#94a3b8' }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: on ? '#6BA82E' : '#e2e8f0' }} />
      {label}
    </span>
  );
}

// =========================================================================
// EMPTY + NEW LOAN MODAL
// =========================================================================
function EmptyState({ onCreate, hasAny }) {
  return (
    <div className="bg-white rounded-2xl p-12 border border-dashed border-slate-200 text-center">
      <div className="w-16 h-16 rounded-full mx-auto flex items-center justify-center mb-4" style={{ background: '#F3FAE8' }}>
        <Plus size={26} style={{ color: '#6BA82E' }} />
      </div>
      <div className="font-serif text-xl font-bold" style={{ color: '#030524' }}>
        {hasAny ? 'No loans match your filters' : 'No loan files yet'}
      </div>
      <p className="text-slate-500 mt-2 max-w-md mx-auto">
        {hasAny ? 'Try adjusting the chip / status / channel filters.' : 'Convert a lead into a loan file or start one from scratch to begin origination.'}
      </p>
      {!hasAny && (
        <button onClick={onCreate} data-testid="new-loan-empty-btn" className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-white" style={{ background: '#070B5F' }}>
          <Plus size={14} /> Create Your First Loan File
        </button>
      )}
    </div>
  );
}

function NewLoanModal({ onClose, onCreated, initialContactId = null }) {
  const [form, setForm] = useState({
    contact_id: initialContactId,
    loan_program: 'conventional',
    loan_purpose: 'purchase',
    occupancy: 'primary',
    property_type: 'sfr',
    purchase_price: '',
    loan_amount: '',
    term_months: 360,
    note_rate: '',
    property_address: emptyAddress(),
  });
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        purchase_price: form.purchase_price === '' ? null : form.purchase_price,
        loan_amount: form.loan_amount === '' ? null : form.loan_amount,
        note_rate: form.note_rate === '' ? null : form.note_rate,
        property_address: addressPayload(form.property_address),
      };
      const loan = await losApi.createLoan(payload);
      toast({ title: 'Loan file created', description: loan.loan_number });
      onCreated(loan);
    } catch (e) {
      toast({ title: 'Failed', description: errorMessage(e) });
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto" data-testid="new-loan-modal">
        <DialogTitle className="font-serif text-xl font-bold mb-1" style={{ color: '#030524' }}>New Loan File</DialogTitle>
        <p className="text-slate-500 text-sm mb-5">Attach an existing contact as the borrower, or fill in details from the 1003 module after creation.</p>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <ContactPicker value={form.contact_id} onChange={(v) => upd('contact_id', v)} testId="new-loan-contact-picker" />
          </div>
          <SelectField label="Loan Program" value={form.loan_program} onChange={(v) => upd('loan_program', v)}
            options={optionsFrom(LOAN_PROGRAM_LABELS)} />
          <SelectField label="Purpose" value={form.loan_purpose} onChange={(v) => upd('loan_purpose', v)}
            options={optionsFrom(LOAN_PURPOSE_LABELS)} />
          <SelectField label="Occupancy" value={form.occupancy} onChange={(v) => upd('occupancy', v)}
            options={optionsFrom(OCCUPANCY_LABELS)} />
          <SelectField label="Property Type" value={form.property_type} onChange={(v) => upd('property_type', v)}
            options={optionsFrom(PROPERTY_TYPE_LABELS)} />
          <TextField label="Purchase Price" kind="money" value={form.purchase_price} onChange={(v) => upd('purchase_price', v ?? '')} />
          <TextField label="Loan Amount" kind="money" value={form.loan_amount} onChange={(v) => upd('loan_amount', v ?? '')} />
          <TextField label="Note Rate %" kind="number" step="0.001" value={form.note_rate} onChange={(v) => upd('note_rate', v ?? '')} />
          <TextField label="Term (months)" kind="number" value={form.term_months} onChange={(v) => upd('term_months', Number(v))} />
          <div className="col-span-2 pt-3 border-t border-slate-100">
            <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Subject Property</div>
          </div>
          <AddressFields value={form.property_address} onChange={(a) => upd('property_address', a)} testIdPrefix="new-loan-address" label="" />
        </div>
        <div className="flex gap-2 mt-5 justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm font-semibold rounded-lg text-slate-600 hover:bg-slate-100" data-testid="new-loan-cancel">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="new-loan-submit"
                  className="px-4 py-2 text-sm font-semibold rounded-lg text-white flex items-center gap-2"
                  style={{ background: '#070B5F' }}>
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Create Loan File
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TextField({ label, value, onChange, type = 'text', step, kind, integer, placeholder }) {
  let field;
  if (kind === 'phone') {
    field = <PhoneInput value={value} onChange={onChange} placeholder={placeholder} className="mt-1" />;
  } else if (kind === 'money') {
    field = <MoneyInput value={value} onChange={onChange} placeholder={placeholder} className="mt-1" />;
  } else if (kind === 'number' || type === 'number') {
    field = <NumberInput integer={!!integer} value={value} onChange={onChange} placeholder={placeholder} className="mt-1" />;
  } else {
    field = <Input type={type} step={step} placeholder={placeholder} value={value ?? ''} onChange={(e) => onChange(e.target.value)} className="mt-1" />;
  }
  return (
    <div>
      <Label className="text-xs text-slate-500">{label}</Label>
      {field}
    </div>
  );
}
function SelectField({ label, value, onChange, options }) {
  return (
    <div>
      <Label className="text-xs text-slate-500">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
        <SelectContent>{options.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}
